import type { FunctionComponent } from 'react'
import { useContext, useState } from 'react'
import { createPortal } from 'react-dom'
import { useParams } from 'react-router-dom'
import { SignUp, TypographyV2 as Typography } from '@which/seatbelt'
import { dynamicGa4DataLayerPush } from '@which/shared'

import classnames from 'classnames'

import { useAddSavedItemMutation, useRemoveSavedItemMutation } from '../../../generated/frontend'
import { ArticleTemplateContext } from '../../../pages/article/ArticleTemplateContext'
import { ReviewsTemplateContext } from '../../../pages/reviews/product-listing/ReviewsTemplateContext'
import {
  ADD_TO_SAVED,
  REMOVE_FROM_SAVED,
  SAVE_ERROR,
} from '../../../pages/reviews/shared/components/ToastNotification/constants'
import { modalImageProps } from '../../../pages/reviews/shared/data'
import {
  getComparisonLimitReached,
  getIsSelectedForComparison,
} from '../../../pages/reviews/utils/comparison'
import type { PageParams } from '../../../routes'
import { useSubscriptionCostInfo } from '../../../shared/hooks/useSubscriptionCostInfo'
import { CompareTrayContext } from '../../contexts/CompareTrayContext'
import { SavedRequestType } from '../../types'
import { usePageProps } from '../../usePageProps'
import { SinglePadlockCta } from '../SinglePadlockCta'
import type { CardedDynamicTableProps } from './CardedDynamicTable'
import styles from './CardedDynamicTable.module.scss'
import { CardedDynamicTableItem } from './CardedDynamicTableItem'

export const CardedDynamicTableWrapper: FunctionComponent<CardedDynamicTableProps> = ({
  dynamicTableId = '',
  logInCTA,
  products,
  signUpCTA,
  stackedCards = false,
  title,
  trackingPrefix = 'dynamic-table',
}) => {
  const subscriptionCostInfo = useSubscriptionCostInfo()
  const { meta } = useContext(ArticleTemplateContext)
  const { compareTrayItems, addProduct, removeProductFromCardHandler } =
    useContext(CompareTrayContext)
  const loginStatus = meta.dataLayer?.[1]?.loginStatus
  const { state } = useContext(ReviewsTemplateContext)
  const { superCategorySlug: categorySlug } = useParams<PageParams>()
  const {
    savedItems,
    userAccessState: { transformTypeDecision },
  } = usePageProps()

  const isPaidMember = ['AUTHENTICATED_FULL_ACCESS', 'FULL_ACCESS'].includes(
    transformTypeDecision ?? ''
  )

  const [modalOpen, setModalOpen] = useState<{ isOpen: boolean; trackingId: string }>({
    isOpen: false,
    trackingId: '',
  })

  const savedProducts = savedItems?.find((i) => i.categorySlug === categorySlug)?.products ?? []

  const [savedProductsState, setSavedProductsState] = useState(
    savedProducts === null ? [] : savedProducts.flatMap((savedProduct) => savedProduct.id)
  )

  const isSavedProduct = (productId: string) => savedProductsState.includes(productId)

  const updateSavedState = (productId: string) => {
    setSavedProductsState((prevSavedProducts) => {
      const newState = prevSavedProducts.includes(productId)
        ? prevSavedProducts.filter((product) => product !== productId)
        : [...prevSavedProducts, productId]

      return newState
    })
  }

  const [addSavedItem, { loading: isAddingSavedProduct }] = useAddSavedItemMutation()
  const [removeSavedItem, { loading: isRemovingSavedProduct }] = useRemoveSavedItemMutation()

  const handleSavedResponse = ({
    responseSuccess,
    requestType,
    productId,
  }: HandleSavedResponseTypes) => {
    const isAddition = requestType === SavedRequestType.ADD

    if (responseSuccess) {
      if (isAddition) {
        state.setToastContext(ADD_TO_SAVED)
      } else {
        const undoRemove = () => {
          addSavedItem({
            variables: {
              item: {
                itemId: productId,
                itemCategory: categorySlug,
                itemType: 'product',
              },
            },
          })
            .then((response) => {
              if (response.data?.addSavedItem) {
                handleSavedResponse({
                  responseSuccess: response.data.addSavedItem.success,
                  requestType: SavedRequestType.ADD,
                  productId,
                })
              }
            })
            .catch(() => {
              handleSavedResponse({
                responseSuccess: false,
                requestType: SavedRequestType.ADD,
                productId,
              })
            })
          updateSavedState(productId)
        }
        state.setToastContext({
          ...REMOVE_FROM_SAVED,
          linkText: 'Undo',
          onClick: undoRemove,
        })
      }
    } else {
      state.setToastContext(SAVE_ERROR)
      updateSavedState(productId)
    }
  }

  const handleSaveClick = (product, tracking) => {
    if (isAddingSavedProduct || isRemovingSavedProduct) {
      return
    }

    const { key: businessKey, categorySlug: productCategorySlug, manufacturer, model } = product
    const { item_spec, modal_tracking } = tracking

    dynamicGa4DataLayerPush({
      event: 'click_save',
      utagid: 'WHC586DP01',
      item_group: 'saved items',
      item_product_id: businessKey,
      item_spec: item_spec,
      item_text: `${manufacturer} ${model}`,
      action_group: `${isSavedProduct(businessKey) ? 'remove from' : 'add to'} save`,
    })

    if (!transformTypeDecision?.includes('NO_ACCESS')) {
      updateSavedState(businessKey)
      isSavedProduct(businessKey)
        ? removeSavedItem({
            variables: {
              itemId: businessKey,
            },
          })
            .then((response) => {
              if (response.data?.removeSavedItem) {
                handleSavedResponse({
                  responseSuccess: response.data.removeSavedItem.success,
                  requestType: SavedRequestType.REMOVE,
                  productId: businessKey,
                })
              }
            })
            .catch(() => {
              handleSavedResponse({
                responseSuccess: false,
                requestType: SavedRequestType.REMOVE,
                productId: businessKey,
              })
            })
        : addSavedItem({
            variables: {
              item: {
                itemId: businessKey,
                itemCategory: productCategorySlug,
                itemType: 'product',
              },
            },
          })
            .then((response) => {
              if (response.data?.addSavedItem) {
                handleSavedResponse({
                  responseSuccess: response.data.addSavedItem.success,
                  requestType: SavedRequestType.ADD,
                  productId: businessKey,
                })
              }
            })
            .catch(() => {
              handleSavedResponse({
                responseSuccess: false,
                requestType: SavedRequestType.ADD,
                productId: businessKey,
              })
            })
    } else {
      setModalOpen({ isOpen: true, trackingId: modal_tracking })
      return
    }
  }

  if (!products?.length) {
    return null
  }

  const brands = new Set()

  products.forEach((product) => brands.add(product?.manufacturer?.slug))

  const brandString = Array.from(brands).join(',')

  return (
    <div
      data-dt-product-count={products.length}
      {...(brandString && { 'data-dt-product-brands': brandString })}
      data-testid="carded-dynamic-table"
      data-dynamic-table-id={dynamicTableId}
      className={styles.container}
    >
      {title && (
        <Typography className="h2-article" tag="h2" textStyle="sb-text-heading-large">
          {title}
        </Typography>
      )}

      <ul
        className={classnames(styles.list, {
          [styles.listLoggedOut]: !isPaidMember && stackedCards,
        })}
      >
        {!isPaidMember && stackedCards && (
          <>
            {CardedDynamicTableItem({
              stackedCards,
              index: 0,
              loginStatus,
              isAllowCompare: isPaidMember,
              trackingPrefix,
              ...products[0],
              isSelectedForComparison: getIsSelectedForComparison({
                compareTrayItems,
                slug: products[0].slug || '',
              }),
              isComparisonLimitReached: getComparisonLimitReached({
                compareTrayItems: compareTrayItems,
              }),
              addProductHandler: addProduct,
              removeProductHandler: removeProductFromCardHandler,
              onSaveClick: () =>
                handleSaveClick(products[0], {
                  item_spec: `${trackingPrefix === 'spp' ? trackingPrefix : 'dt'} product card`,
                  modal_tracking: 'mainsavebutton',
                }),
              saved: isSavedProduct(products[0].key || ''),
            })}
            <li key={`${dynamicTableId}-signup-panel}`} className={styles.listItem}>
              <SinglePadlockCta
                buttonCopy="Unlock reviews"
                displayButtonIcon={false}
                displayStandfirst={false}
                horizontal={true}
                lockedPadlockIcon={false}
                title="Sign up to get instant access to all reviews, scores, and expert recommendations"
              />
            </li>
          </>
        )}

        {isPaidMember || (!isPaidMember && !stackedCards)
          ? products.map((product, index) => {
              const isSelectedForComparison = getIsSelectedForComparison({
                compareTrayItems,
                slug: product.slug || '',
              })

              return CardedDynamicTableItem({
                stackedCards,
                index,
                loginStatus,
                isAllowCompare: isPaidMember,
                logInCTA,
                signUpCTA,
                trackingPrefix,
                ...product,
                isSelectedForComparison,
                isComparisonLimitReached: getComparisonLimitReached({
                  compareTrayItems: compareTrayItems,
                }),
                addProductHandler: addProduct,
                removeProductHandler: removeProductFromCardHandler,
                onSaveClick: () =>
                  handleSaveClick(product, {
                    item_spec: `${trackingPrefix === 'spp' ? trackingPrefix : 'dt'} product card`,
                    modal_tracking: 'mainsavebutton',
                  }),
                saved: isSavedProduct(product.key || ''),
              })
            })
          : null}
      </ul>

      {modalOpen.isOpen &&
        createPortal(
          <SignUp
            className={styles.modal}
            image={modalImageProps}
            overline={subscriptionCostInfo.overline}
            title={subscriptionCostInfo.title}
            subtitle={subscriptionCostInfo.subtitle}
            subscribeCTALink={subscriptionCostInfo.subscribeCTALink}
            closeModal={() => setModalOpen({ isOpen: false, trackingId: '' })}
            modalLinkDatalayerTrackingId={modalOpen.trackingId}
          />,
          document.body
        )}
    </div>
  )
}

///////// IMPLEMENTATION /////////

type HandleSavedResponseTypes = {
  responseSuccess: boolean
  requestType: SavedRequestType
  productId: string
}
